import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Brain, Lightbulb, Rocket, Target, CheckCircle, XCircle, Trophy, TrendingUp, Users, BookOpen, DollarSign, X, Phone, Mail, MessageCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { fetchAIBusinessIdeas, fetchClients, respondToAIBusinessIdea } from '../../../redux/api';

const phaseOrder = ['Strategija', 'Marketinške aktivnosti', 'Prodaja', 'Operativa', 'Automatizacija', 'Analitika', 'Druge faze'];

const groupIdeasByPhase = (ideas = []) => {
  const map = {};
  ideas.forEach((idea) => {
    const phase = idea.phase?.trim() || 'Druge faze';
    if (!map[phase]) {
      map[phase] = [];
    }
    map[phase].push(idea);
  });

  const sortedPhases = Object.keys(map).sort((a, b) => {
    const idxA = phaseOrder.indexOf(a);
    const idxB = phaseOrder.indexOf(b);
    return (idxA === -1 ? Number.MAX_SAFE_INTEGER : idxA) - (idxB === -1 ? Number.MAX_SAFE_INTEGER : idxB);
  });

  return sortedPhases.map((phase) => ({
    phase,
    ideas: map[phase].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)),
  }));
};

const formatActions = (actionSteps = [], formatFn = (value) => value) => {
  if (!actionSteps.length) {
    return null;
  }
  return (
    <ul className="list-disc list-inside space-y-1 text-xs sm:text-sm text-gray-100">
      {actionSteps.map((step, index) => (
        <li key={`${step}-${index}`} className="leading-relaxed">
          {formatFn(step)}
        </li>
      ))}
    </ul>
  );
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleString('sr-RS', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const AIBusinessView = () => {
  const { user } = useSelector((state) => state.auth);
  const role = user?.result?.role || user?.role || '';
  const isSuperAdmin = role === 'superadmin';

  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPhase, setExpandedPhase] = useState('');
  const [clientCount, setClientCount] = useState(null);
  const [respondingIdeaId, setRespondingIdeaId] = useState(null);

  // Filtrirati ideje vezane za Fazu 1 ako je faza već završena
  const filteredIdeas = useMemo(() => {
    if (isSuperAdmin || clientCount === null || clientCount < 100) {
      return ideas;
    }
    // Sakrij ideje koje su vezane za Fazu 1 (prvih 100 klijenata) kada je faza završena
    return ideas.filter(idea => {
      const title = idea.title?.toLowerCase() || '';
      const summary = idea.summary?.toLowerCase() || '';
      const targetClientCount = idea.targetClientCount || idea.progress?.target;
      
      // Proveri da li ideja sadrži reference na "faza 1", "prvih 100", "100 klijenata" itd.
      const isPhase1Idea = 
        title.includes('faza 1') || 
        title.includes('prvih 100') ||
        (title.includes('100') && (title.includes('klijenat') || title.includes('klijent'))) ||
        summary.includes('prvih 100 klijenata') ||
        summary.includes('faza 1') ||
        (targetClientCount === 100 && clientCount >= 100); // Ako je target 100 i već je dostignut
      
      return !isPhase1Idea;
    });
  }, [ideas, clientCount, isSuperAdmin]);

  const phases = useMemo(() => groupIdeasByPhase(filteredIdeas), [filteredIdeas]);

  const formatClientLabel = useCallback((count) => {
    if (count === null) {
      return '0 klijenata';
    }
    const absCount = Math.abs(count);
    if (absCount === 1) {
      return `${count} klijent`;
    }
    const lastTwoDigits = absCount % 100;
    const lastDigit = absCount % 10;
    if (lastTwoDigits >= 12 && lastTwoDigits <= 14) {
      return `${count} klijenata`;
    }
    if (lastDigit >= 2 && lastDigit <= 4) {
      return `${count} klijenta`;
    }
    return `${count} klijenata`;
  }, []);

  const applyDynamicText = useCallback(
    (text) => {
      if (!text) return text;
      let result = text;

      if (clientCount !== null) {
        const clientLabel = formatClientLabel(clientCount);
        result = result.replace(/\{\s*iz\s*baze\s*\}\s*klijenata/gi, clientLabel);
        result = result.replace(/\{\{\s*clientCount\s*\}\}/gi, String(clientCount));
        result = result.replace(/\{\{\s*clientCountLabel\s*\}\}/gi, clientLabel);
        result = result.replace(/\{\s*clientCount\s*\}/gi, String(clientCount));
        result = result.replace(/\{\s*clientCountLabel\s*\}/gi, clientLabel);
        result = result.replace(/\{\s*iz\s*baze\s*\}/gi, clientLabel);

        result = result.replace(/\(\s*0\s+klijen[ataei]+\b([^)]*)\)/gi, (_match, rest) => `(${clientLabel}${rest || ''})`);
        result = result.replace(/\b0\s+klijen[ataei]+\b/gi, clientLabel);
      }

      return result;
    },
    [clientCount, formatClientLabel]
  );

  useEffect(() => {
    const loadIdeas = async () => {
      try {
        setLoading(true);
        const ideasPromise = fetchAIBusinessIdeas();
        const clientsPromise = isSuperAdmin ? Promise.resolve(null) : fetchClients();

        const [ideasResult, clientsResult] = await Promise.allSettled([ideasPromise, clientsPromise]);

        if (ideasResult.status === 'fulfilled') {
          const data = Array.isArray(ideasResult.value?.data) ? ideasResult.value.data : [];
          setIdeas(data);
          if (data.length > 0) {
            setExpandedPhase(data[0].phase || 'Druge faze');
          }
        } else {
          console.error('Greška pri učitavanju AI biznis ideja:', ideasResult.reason);
          toast.error('Ne možemo da učitamo AI biznis smernice. Proveri vezu sa serverom.');
        }

        if (!isSuperAdmin && clientsResult.status === 'fulfilled') {
          const clientsData = Array.isArray(clientsResult.value?.data) ? clientsResult.value.data : [];
          setClientCount(clientsData.length);
        } else if (!isSuperAdmin && clientsResult.status === 'rejected') {
          console.warn('Ne možemo da učitamo listu klijenata za AI smernice:', clientsResult.reason);
        }
      } catch (error) {
        console.error('Greška pri učitavanju AI biznis ideja:', error);
        toast.error('Ne možemo da učitamo AI biznis smernice. Proveri vezu sa serverom.');
      } finally {
        setLoading(false);
      }
    };

    loadIdeas();
  }, [isSuperAdmin]);

  // Osveži broj klijenata svakih 30 sekundi (za real-time praćenje)
  useEffect(() => {
    if (isSuperAdmin) return;

    const refreshClientCount = async () => {
      try {
        const clientsResult = await fetchClients();
        const clientsData = Array.isArray(clientsResult?.data) ? clientsResult.data : [];
        setClientCount(clientsData.length);
      } catch (error) {
        console.warn('Ne možemo da osvežimo broj klijenata:', error);
      }
    };

    const interval = setInterval(refreshClientCount, 30000); // Svakih 30 sekundi
    return () => clearInterval(interval);
  }, [isSuperAdmin]);

  const handleIdeaResponse = useCallback(
    async (ideaId, status) => {
      if (isSuperAdmin) return;

      try {
        setRespondingIdeaId(`${ideaId}-${status}`);
        const { data } = await respondToAIBusinessIdea({ id: ideaId, status });

        setIdeas((prevIdeas) =>
          prevIdeas.map((idea) =>
            idea.id === ideaId
              ? {
                  ...idea,
                  userResponse: {
                    status: data.status,
                    updatedAt: data.updatedAt,
                  },
                  responsesSummary: data.summary || idea.responsesSummary,
                }
              : idea
          )
        );

        toast.success(
          status === 'accepted'
            ? 'Hvala! Evidentirali smo da pokrećeš ovu fazu.'
            : 'Razumemo, zabeležili smo da trenutno preskačeš ovu fazu.'
        );
      } catch (error) {
        console.error('Greška pri slanju odgovora za AI biznis ideju:', error);
        const message = error.response?.data?.message || 'Ne možemo da sačuvamo odgovor. Pokušaj ponovo.';
        toast.error(message);
      } finally {
        setRespondingIdeaId(null);
      }
    },
    [isSuperAdmin]
  );

  const renderUserResponse = (idea) => {
    if (isSuperAdmin) return null;

    const currentStatus = idea.userResponse?.status;
    const statusLabel =
      currentStatus === 'accepted'
        ? 'Pokrećemo ovu fazu'
        : currentStatus === 'declined'
        ? 'Za sada preskačemo ovu fazu'
        : 'Nije odlučeno';

    const isAccepting = respondingIdeaId === `${idea.id}-accepted`;
    const isDeclining = respondingIdeaId === `${idea.id}-declined`;

    // Get progress info
    const progress = idea.progress || idea.userResponse?.progress;
    const isCompleted = idea.userResponse?.isCompleted || progress?.isCompleted || false;

    return (
      <div className="bg-slate-800/70 border border-emerald-600/30 rounded-xl p-4 space-y-3">
        {/* Progress indicator for ideas with targetClientCount */}
        {progress && (
          <div className="flex items-center justify-between gap-4 p-3 bg-slate-900/50 rounded-lg border border-slate-700">
            <div className="flex items-center gap-3">
              <div className={`flex items-center justify-center w-6 h-6 rounded border-2 ${
                isCompleted 
                  ? 'bg-emerald-600 border-emerald-500' 
                  : 'bg-transparent border-slate-600'
              }`}>
                {isCompleted && (
                  <CheckCircle className="h-4 w-4 text-white" />
                )}
              </div>
              <div>
                <span className="text-sm font-semibold text-white">
                  Napredak: {progress.current} / {progress.target} klijenata
                </span>
                {isCompleted && (
                  <span className="block text-xs text-emerald-300 mt-1">
                    ✓ Faza završena!
                  </span>
                )}
              </div>
            </div>
            {progress.current < progress.target && (
              <div className="flex-1 max-w-xs">
                <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-emerald-500 transition-all duration-300"
                    style={{ width: `${(progress.current / progress.target) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="text-sm text-slate-200">
            <span className="font-semibold text-white">Tvoj status:</span> {statusLabel}
            {idea.userResponse?.updatedAt && (
              <span className="block text-xs text-slate-400">
                Ažurirano: {formatDate(idea.userResponse.updatedAt)}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => handleIdeaResponse(idea.id, 'accepted')}
              disabled={isAccepting}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs sm:text-sm transition-colors ${
                currentStatus === 'accepted'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/40 hover:bg-emerald-500/30'
              } ${isAccepting ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              <CheckCircle className="h-4 w-4" />
              Pokrećemo fazu
            </button>
            <button
              type="button"
              onClick={() => handleIdeaResponse(idea.id, 'declined')}
              disabled={isDeclining}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs sm:text-sm transition-colors ${
                currentStatus === 'declined'
                  ? 'bg-red-600 text-white'
                  : 'bg-red-500/10 text-red-200 border border-red-500/40 hover:bg-red-500/20'
              } ${isDeclining ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              <XCircle className="h-4 w-4" />
              Još ne krećemo
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderResponsesSummary = (idea) => {
    if (!isSuperAdmin || !idea.responsesSummary) return null;

    const { acceptedCount, declinedCount, accepted, declined } = idea.responsesSummary;

    return (
      <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 space-y-3 text-xs sm:text-sm text-slate-200">
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-emerald-300" />
            <span className="font-semibold text-emerald-200">Pokreću: {acceptedCount}</span>
          </div>
          <div className="flex items-center gap-2">
            <XCircle className="h-4 w-4 text-red-300" />
            <span className="font-semibold text-red-200">Preskaču: {declinedCount}</span>
          </div>
        </div>

        {(accepted.length > 0 || declined.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {accepted.length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-wide text-emerald-300 mb-2">Pokreću</p>
                <ul className="space-y-1">
                  {accepted.map((entry) => (
                    <li
                      key={`${idea.id}-accepted-${entry.companyId}`}
                      className="flex items-center justify-between gap-2 bg-slate-900/80 border border-slate-700 rounded-lg px-3 py-2"
                    >
                      <span className="font-medium text-slate-100">{entry.companyName}</span>
                      <span className="text-[11px] text-slate-400">{formatDate(entry.respondedAt)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {declined.length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-wide text-red-300 mb-2">Za sada preskaču</p>
                <ul className="space-y-1">
                  {declined.map((entry) => (
                    <li
                      key={`${idea.id}-declined-${entry.companyId}`}
                      className="flex items-center justify-between gap-2 bg-slate-900/80 border border-slate-700 rounded-lg px-3 py-2"
                    >
                      <span className="font-medium text-slate-100">{entry.companyName}</span>
                      <span className="text-[11px] text-slate-400">{formatDate(entry.respondedAt)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };


  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 px-4 sm:px-6 py-6">
      <div className="max-w-6xl mx-auto space-y-8">
        <header className="bg-gradient-to-r from-slate-800 to-slate-700 border border-slate-600/70 rounded-2xl p-6 sm:p-8 shadow-lg">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-emerald-500/20 rounded-full border border-emerald-400/40">
                  <Brain className="h-7 w-7 text-emerald-300" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-white">AI Biznis</h1>
                  <p className="text-sm sm:text-base text-emerald-100/90">
                    Praktične biznis ideje koje možemo pokrenuti odmah, uz AI asistenciju koja ubrzava svaki korak.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs sm:text-sm text-slate-200">
                <div className="flex items-center gap-2 bg-slate-800/70 border border-slate-700 rounded-xl px-4 py-3">
                  <Lightbulb className="h-5 w-5 text-yellow-400 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-white">Ideje sa dokazanim potencijalom</p>
                    <p className="text-slate-300">Svaka faza sadrži konkretne AI korake.</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-slate-800/70 border border-slate-700 rounded-xl px-4 py-3">
                  <Rocket className="h-5 w-5 text-orange-400 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-white">Brza implementacija</p>
                    <p className="text-slate-300">Od ideje do testirane ponude za manje od nedelju dana.</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-slate-800/70 border border-slate-700 rounded-xl px-4 py-3">
                  <Target className="h-5 w-5 text-sky-400 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-white">AI asistencija po fazama</p>
                    <p className="text-slate-300">Savet i alati u svakom koraku procesa.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-400"></div>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Faza 1 - Prvih 100 klijenata */}
            {!isSuperAdmin && (
              <PhaseTracker 
                clientCount={clientCount}
                phaseNumber={1}
                target={100}
                title="Faza 1 - Prvih 100 klijenata"
                description="Cilj je doći do baze od 100 klijenata. Trenutno u bazi ima manje od 100 klijenata. Da bismo došli do 100, prepisujemo sve brojeve telefona iz imenika i iz konverzacija poruka i unosimo ih kroz sekciju 'Klijenti'."
                formatClientLabel={formatClientLabel}
              />
            )}

            {/* Faza 2 - Automatski se prikazuje kada je Faza 1 završena */}
            {!isSuperAdmin && clientCount !== null && clientCount >= 100 && (
              <PhaseTracker 
                clientCount={clientCount}
                phaseNumber={2}
                target={250}
                title="Faza 2 - Rast baze klijenata"
                description="Čestitamo! Dostigli ste prvu fazu od 100 klijenata. Sada je vreme za rast! Cilj je doći do 250 klijenata kroz ciljane marketinške kampanje, preporuke postojećih klijenata i proširenje usluga. Koristite AI asistenciju za personalizovane kampanje i optimizaciju konverzije."
                formatClientLabel={formatClientLabel}
                isActive={true}
              />
            )}

            {/* Prikaži poruku samo ako nema ideja I nema aktivnih faza */}
            {phases.length === 0 && ideas.length === 0 ? (
              <div className="bg-slate-800/70 border border-slate-700 rounded-2xl p-8 text-center text-sm text-slate-300">
                Još uvek nema definisanih AI biznis smernica. Kontaktiraj superadmin tim da se dodaju prve ideje.
              </div>
            ) : phases.length > 0 ? (
              <>
            {phases.map(({ phase, ideas: phaseIdeas }) => {
              const isExpanded = expandedPhase === phase;
              return (
                <section key={phase} className="bg-slate-900/80 border border-slate-700 rounded-2xl overflow-hidden shadow-lg">
                  <button
                    type="button"
                    onClick={() => setExpandedPhase(isExpanded ? '' : phase)}
                    className="w-full flex items-center justify-between px-4 sm:px-6 py-4 bg-slate-800/70 hover:bg-slate-800 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-lg">{isExpanded ? '▼' : '▶'}</span>
                      <div>
                        <h2 className="text-lg sm:text-xl font-semibold text-white">{phase}</h2>
                        <p className="text-xs sm:text-sm text-slate-300">{phaseIdeas.length} ideja spremnih za implementaciju</p>
                      </div>
                    </div>
                    <span className="text-xs font-medium bg-emerald-500/20 text-emerald-200 border border-emerald-500/40 rounded-full px-3 py-1">
                      AI vodič
                    </span>
                  </button>

                  {isExpanded && (
                    <div className="divide-y divide-slate-800">
                      {phaseIdeas.map((idea) => (
                        <article key={idea.id} className="p-4 sm:p-6 space-y-4">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                            <div className="space-y-2">
                              <h3 className="text-xl font-semibold text-white">{idea.title}</h3>
                              {idea.summary && <p className="text-sm text-slate-200 max-w-3xl">{applyDynamicText(idea.summary)}</p>}
                            </div>
                            <div className="text-xs text-slate-400 text-right sm:text-left">
                              <p className="uppercase tracking-wide text-slate-500">Poslednja izmena</p>
                              <p>{formatDate(idea.updatedAt)}</p>
                            </div>
                          </div>

                          {idea.actionSteps?.length > 0 && (
                            <div className="bg-slate-800/70 border border-slate-700 rounded-xl p-4">
                              <p className="text-xs font-semibold uppercase text-slate-300 mb-2">Koraci za primenu</p>
                              {formatActions(idea.actionSteps, applyDynamicText)}
                            </div>
                          )}

                          {(idea.aiAssist || idea.impact || idea.resources) && (
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                              {idea.aiAssist && (
                                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 text-xs sm:text-sm text-blue-100">
                                  <p className="font-semibold text-blue-200 mb-1">AI asistencija</p>
                                  <p className="leading-relaxed">{applyDynamicText(idea.aiAssist)}</p>
                                </div>
                              )}
                              {idea.impact && (
                                <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 text-xs sm:text-sm text-purple-100">
                                  <p className="font-semibold text-purple-200 mb-1">Očekivani uticaj</p>
                                  <p className="leading-relaxed">{applyDynamicText(idea.impact)}</p>
                                </div>
                              )}
                              {idea.resources && (
                                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-xs sm:text-sm text-emerald-100">
                                  <p className="font-semibold text-emerald-200 mb-1">Resursi / alati</p>
                                  <p className="leading-relaxed">{applyDynamicText(idea.resources)}</p>
                                </div>
                              )}
                            </div>
                          )}

                          {idea.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-2 text-[11px] text-slate-300">
                              {idea.tags.map((tag) => (
                                <span key={tag} className="px-3 py-1 bg-slate-800 rounded-full border border-slate-700">
                                  #{tag}
                                </span>
                              ))}
                            </div>
                          )}

                          {renderUserResponse(idea)}
                          {renderResponsesSummary(idea)}
                        </article>
                      ))}
                    </div>
                  )}
                </section>
              );
            })}
              </>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

// Komponenta za praćenje faza
const PhaseTracker = ({ 
  clientCount, 
  phaseNumber, 
  target, 
  title, 
  description, 
  formatClientLabel,
  isActive = false 
}) => {
  const current = clientCount || 0;
  const isCompleted = current >= target;
  const progress = Math.min((current / target) * 100, 100);

  useEffect(() => {
    if (isCompleted && phaseNumber === 1) {
      // Check if we've already shown this notification
      const notificationKey = `phase1_completed_${target}`;
      const hasShownNotification = localStorage.getItem(notificationKey);
      
      if (!hasShownNotification) {
        toast.success(`🎉 Čestitamo! Faza 1 je kompletirana! Dostigli ste ${target} klijenata!`, {
          autoClose: 5000,
        });
        // Mark as shown in localStorage
        localStorage.setItem(notificationKey, 'true');
      }
    }
  }, [isCompleted, phaseNumber, target]);

  return (
    <div className={`border-2 rounded-2xl overflow-hidden shadow-xl transition-all duration-500 ${
      isCompleted 
        ? 'bg-gradient-to-br from-red-900/40 to-red-800/30 border-red-500/60' 
        : isActive
        ? 'bg-gradient-to-br from-emerald-900/40 to-emerald-800/30 border-emerald-500/60'
        : 'bg-slate-900/80 border-slate-700'
    }`}>
      <div className={`p-6 ${isCompleted ? 'bg-red-900/20' : isActive ? 'bg-emerald-900/20' : 'bg-slate-800/70'}`}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-3 flex-1">
            {isCompleted ? (
              <div className="p-2 bg-red-500/20 rounded-full border-2 border-red-500">
                <Trophy className="h-6 w-6 text-red-400" />
              </div>
            ) : (
              <div className={`p-2 rounded-full border-2 ${
                isActive 
                  ? 'bg-emerald-500/20 border-emerald-500' 
                  : 'bg-slate-700/50 border-slate-600'
              }`}>
                <Target className={`h-6 w-6 ${isActive ? 'text-emerald-400' : 'text-slate-400'}`} />
              </div>
            )}
            <div className="flex-1">
              <h2 className={`text-xl font-bold mb-1 ${
                isCompleted ? 'text-red-200' : isActive ? 'text-emerald-200' : 'text-white'
              }`}>
                {title}
              </h2>
              {isCompleted && phaseNumber === 1 && (
                <p className="text-red-300 font-semibold text-sm flex items-center gap-2 mt-1">
                  <CheckCircle className="h-4 w-4" />
                  Faza kompletirana!
                </p>
              )}
            </div>
            {isCompleted && (
              <div className="flex items-center gap-2 px-3 py-1 bg-red-500/30 border border-red-500/50 rounded-full">
                <Trophy className="h-4 w-4 text-red-300" />
                <span className="text-sm font-semibold text-red-200">Završeno</span>
              </div>
            )}
          </div>
        </div>

        <p className={`text-sm mb-4 leading-relaxed ${
          isCompleted ? 'text-red-100/90' : isActive ? 'text-emerald-100/90' : 'text-slate-300'
        }`}>
          {description}
        </p>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className={`h-5 w-5 ${
                isCompleted ? 'text-red-400' : isActive ? 'text-emerald-400' : 'text-slate-400'
              }`} />
              <span className={`text-sm font-semibold ${
                isCompleted ? 'text-red-200' : isActive ? 'text-emerald-200' : 'text-slate-200'
              }`}>
                Napredak: {formatClientLabel(current)} / {target} klijenata
              </span>
            </div>
            <span className={`text-xs font-medium px-2 py-1 rounded ${
              isCompleted 
                ? 'bg-red-500/30 text-red-200 border border-red-500/50' 
                : isActive
                ? 'bg-emerald-500/30 text-emerald-200 border border-emerald-500/50'
                : 'bg-slate-700/50 text-slate-300 border border-slate-600'
            }`}>
              {Math.round(progress)}%
            </span>
          </div>

          <div className="relative h-3 bg-slate-800/50 rounded-full overflow-hidden border border-slate-700/50">
            <div 
              className={`h-full transition-all duration-500 rounded-full ${
                isCompleted 
                  ? 'bg-gradient-to-r from-red-500 to-red-600' 
                  : isActive
                  ? 'bg-gradient-to-r from-emerald-500 to-emerald-600'
                  : 'bg-gradient-to-r from-blue-500 to-blue-600'
              }`}
              style={{ width: `${progress}%` }}
            />
            {isCompleted && (
              <div className="absolute inset-0 bg-red-400/20 animate-pulse" />
            )}
          </div>

          {isCompleted && phaseNumber === 1 && (
            <div className="mt-4 p-4 bg-red-500/20 border border-red-500/40 rounded-xl">
              <p className="text-red-200 font-semibold text-sm flex items-center gap-2">
                <Trophy className="h-5 w-5" />
                Uspešno ste dostigli prvu fazu! Faza 2 je sada aktivna.
              </p>
            </div>
          )}

          {isActive && !isCompleted && phaseNumber === 2 && (
            <div className="mt-4 space-y-3">
              <p className="text-emerald-200 text-sm flex items-center gap-2 mb-3">
                <TrendingUp className="h-4 w-4" />
                Nastavite sa rastom! Koristite AI asistenciju za optimizaciju kampanja.
              </p>
              
              {/* Marketing Buttons */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FreeMarketingGuide phaseNumber={phaseNumber} />
                <PaidMarketingContact phaseNumber={phaseNumber} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Besplatan Marketing Vodič Komponenta
const FreeMarketingGuide = ({ phaseNumber }) => {
  const [showModal, setShowModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  if (phaseNumber !== 2) return null;

  const freeMarketingTips = [
    {
      title: "Društvene mreže",
      items: [
        "Kreirajte besplatne naloge na Facebook, Instagram, LinkedIn",
        "Objavljujte redovno korisne savete i informacije",
        "Koristite hashtag-ove relevantne za vašu industriju",
        "Učestvujte u lokalnim grupama i zajednicama",
        "Podelite uspešne slučajeve i zadovoljne klijente"
      ]
    },
    {
      title: "Preporuke postojećih klijenata",
      items: [
        "Zamolite zadovoljne klijente da vas preporuče",
        "Kreirajte program lojalnosti sa nagradama za preporuke",
        "Podelite linkove za preporuke kroz email ili SMS",
        "Organizujte akcije 'Dovedi prijatelja i dobij popust'"
      ]
    },
    {
      title: "Lokalni marketing",
      items: [
        "Registrujte se na Google My Business",
        "Kreirajte besplatne oglase na lokalnim sajtovima",
        "Učestvujte na lokalnim sajmovima i događajima",
        "Kontaktirajte lokalne medije za prilike za publicitet",
        "Kreirajte partnerstva sa komplementarnim biznisima"
      ]
    },
    {
      title: "Email marketing",
      items: [
        "Koristite besplatne alate kao što su Mailchimp (do 2000 kontakata)",
        "Kreirajte newsletter sa korisnim savetima",
        "Pošaljite follow-up emailove nakon usluga",
        "Koristite personalizovane poruke za bolje rezultate"
      ]
    },
    {
      title: "SEO optimizacija",
      items: [
        "Optimizujte svoj web sajt sa relevantnim ključnim rečima",
        "Kreirajte blog sa korisnim člancima",
        "Dobijte linkove sa drugih sajtova (backlinks)",
        "Koristite lokalni SEO za bolju vidljivost"
      ]
    },
    {
      title: "Sadržaj marketing",
      items: [
        "Kreirajte besplatne vodiče i e-knjige",
        "Snimite YouTube video tutorijale",
        "Podelite korisne savete kroz blog postove",
        "Organizujte besplatne webinare ili radionice"
      ]
    }
  ];

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg text-sm font-medium"
      >
        <BookOpen className="h-5 w-5" />
        Besplatan Marketing Vodič
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
            <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-600 rounded-lg">
                  <BookOpen className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Besplatan Marketing Vodič</h2>
                  <p className="text-sm text-gray-400">Načini kako besplatno doći do novih korisnika</p>
                </div>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {freeMarketingTips.map((section, index) => (
                <div key={index} className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                    <Lightbulb className="h-5 w-5 text-emerald-400" />
                    {section.title}
                  </h3>
                  <ul className="space-y-2">
                    {section.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start gap-2 text-sm text-gray-300">
                        <CheckCircle className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              {/* Besplatne Konsultacije Dugme */}
              <div className="pt-4 border-t border-gray-700">
                <button
                  onClick={() => {
                    setShowModal(false);
                    setShowContactModal(true);
                  }}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg text-sm font-medium"
                >
                  <MessageCircle className="h-5 w-5" />
                  Besplatne Konsultacije
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Kontakt Modal za Besplatne Konsultacije */}
      {showContactModal && (
        <FreeConsultationContactModal onClose={() => setShowContactModal(false)} />
      )}
    </>
  );
};

// Plaćeni Marketing Kontakt Komponenta
const PaidMarketingContact = ({ phaseNumber }) => {
  const [showModal, setShowModal] = useState(false);

  if (phaseNumber !== 2) return null;

  const handleCall = () => {
    window.location.href = 'tel:+381600301207';
  };

  const handleEmail = () => {
    window.location.href = 'mailto:info@spintasker.com?subject=Marketing Kampanja - Upit';
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/381600301207?text=Zdravo, zanima me vaša usluga marketing kampanja`, '_blank');
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg text-sm font-medium"
      >
        <DollarSign className="h-5 w-5" />
        Plaćeni Marketing
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full border border-gray-700">
            <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-600 rounded-lg">
                  <DollarSign className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Plaćeni Marketing</h2>
                  <p className="text-sm text-gray-400">Kontaktirajte nas za profesionalne kampanje</p>
                </div>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="bg-gradient-to-br from-blue-900/30 to-blue-800/20 rounded-lg p-6 border border-blue-700/50">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Rocket className="h-5 w-5 text-blue-400" />
                  SpinTasker Marketing Tim
                </h3>
                <p className="text-gray-300 mb-4">
                  Naš tim će vam pomoći da kreirate profesionalne marketinške kampanje prilagođene vašem biznisu. 
                  Pomoći ćemo vam da dostignete svoj cilj od 250 klijenata kroz ciljane kampanje i optimizaciju.
                </p>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-gray-700/50 rounded-lg">
                    <Phone className="h-5 w-5 text-blue-400" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-400">Telefon</p>
                      <p className="text-white font-medium">+381 60 030 1207</p>
                    </div>
                    <button
                      onClick={handleCall}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
                    >
                      Pozovi
                    </button>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-gray-700/50 rounded-lg">
                    <Mail className="h-5 w-5 text-blue-400" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-400">Email</p>
                      <p className="text-white font-medium">info@spintasker.com</p>
                    </div>
                    <button
                      onClick={handleEmail}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
                    >
                      Pošalji Email
                    </button>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-gray-700/50 rounded-lg">
                    <MessageCircle className="h-5 w-5 text-blue-400" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-400">WhatsApp</p>
                      <p className="text-white font-medium">+381 60 030 1207</p>
                    </div>
                    <button
                      onClick={handleWhatsApp}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm font-medium"
                    >
                      Otvori WhatsApp
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-600">
                <h4 className="text-sm font-semibold text-white mb-2">Šta nudimo:</h4>
                <ul className="space-y-1 text-sm text-gray-300">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>Personalizovane marketinške strategije</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>Kreiranje i upravljanje kampanjama</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>Optimizacija konverzije</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>Analitika i praćenje rezultata</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>Ručno kreiranje kampanja prilagođenih vašem biznisu</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Besplatne Konsultacije Kontakt Modal
const FreeConsultationContactModal = ({ onClose }) => {
  const handleCall = () => {
    window.location.href = 'tel:+381600301207';
  };

  const handleEmail = () => {
    window.location.href = 'mailto:info@spintasker.com?subject=Besplatne Marketing Konsultacije - Upit';
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/381600301207?text=Zdravo, zanimaju me besplatne marketing konsultacije`, '_blank');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full border border-gray-700">
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-600 rounded-lg">
              <MessageCircle className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Besplatne Konsultacije</h2>
              <p className="text-sm text-gray-400">Kontaktirajte SpinTasker tim</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="bg-gradient-to-br from-emerald-900/30 to-emerald-800/20 rounded-lg p-6 border border-emerald-700/50">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Rocket className="h-5 w-5 text-emerald-400" />
              SpinTasker Marketing Tim
            </h3>
            <p className="text-gray-300 mb-4">
              Kontaktirajte nas za besplatne konsultacije o marketinškim strategijama. Naš tim će vam pomoći 
              da identifikujete najbolje besplatne načine za rast vašeg biznisa i dostizanje cilja od 250 klijenata.
            </p>
            
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-gray-700/50 rounded-lg">
                <Phone className="h-5 w-5 text-emerald-400" />
                <div className="flex-1">
                  <p className="text-sm text-gray-400">Telefon</p>
                  <p className="text-white font-medium">+381 60 030 1207</p>
                </div>
                <button
                  onClick={handleCall}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  Pozovi
                </button>
              </div>

              <div className="flex items-center gap-3 p-3 bg-gray-700/50 rounded-lg">
                <Mail className="h-5 w-5 text-emerald-400" />
                <div className="flex-1">
                  <p className="text-sm text-gray-400">Email</p>
                  <p className="text-white font-medium">info@spintasker.com</p>
                </div>
                <button
                  onClick={handleEmail}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  Pošalji Email
                </button>
              </div>

              <div className="flex items-center gap-3 p-3 bg-gray-700/50 rounded-lg">
                <MessageCircle className="h-5 w-5 text-emerald-400" />
                <div className="flex-1">
                  <p className="text-sm text-gray-400">WhatsApp</p>
                  <p className="text-white font-medium">+381 60 030 1207</p>
                </div>
                <button
                  onClick={handleWhatsApp}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  Otvori WhatsApp
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIBusinessView;


